import React, { useRef, useState, useEffect } from 'react';
import './MysticaReplica.css';
import DecryptedText from '../blocks/DecryptedText/DecryptedText';
import Carousel, { CarouselItem } from '../blocks/Carousel/Carousel';
import AnimatedList from '../blocks/AnimatedList/AnimatedList';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import { CSSProperties } from 'react';
import { detectBrowserLanguage, getTranslations, Language } from "../utils/localization";

const languageSwitcherStyle: CSSProperties = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1000,
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden'
};

const MysticaReplica: React.FC = () => {
  // Language state
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState(getTranslations('en').mysticaReplica);
  
  // State to track collisions between side navigation and content
  const [sideNavVisible, setSideNavVisible] = useState(true);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const collisionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect browser language on component mount
  useEffect(() => {
    const detectedLanguage = detectBrowserLanguage();
    setLanguage(detectedLanguage);
    setTranslations(getTranslations(detectedLanguage).mysticaReplica);
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
    setTranslations(getTranslations(lang).mysticaReplica);
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sectionRefs = {
    play: useRef<HTMLElement>(null),
    behind: useRef<HTMLElement>(null),
    showcase: useRef<HTMLElement>(null),
    gameplay: useRef<HTMLElement>(null),
    narrative: useRef<HTMLElement>(null),
    challenges: useRef<HTMLElement>(null)
  };

  const [activeSection, setActiveSection] = useState<number>(0);

  const scrollToSection = (item: string, index: number) => {
    const refKeys = Object.keys(sectionRefs);
    const ref = sectionRefs[refKeys[index] as keyof typeof sectionRefs];
    
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(index);
    }
  };

  // Check for collisions between side navigation and each section
  useEffect(() => {
    const checkCollision = () => {
      if (!sideNavRef.current) return;
      
      const sideNavRect = sideNavRef.current.getBoundingClientRect();
      let hasCollision = false;
      
      // Add a small margin to detect collision before actual overlap
      const collisionMargin = 20; // 20px margin
      
      // Check collision with each section
      Object.values(sectionRefs).forEach(sectionRef => {
        if (!sectionRef.current) return;
        
        const sectionRect = sectionRef.current.getBoundingClientRect();
        
        // Calculate horizontal overlap with margin
        const horizontalOverlap = !(
          sideNavRect.right < sectionRect.left - collisionMargin || 
          sideNavRect.left > sectionRect.right + collisionMargin
        );
        
        // Calculate vertical overlap (only consider sections in viewport)
        const verticalOverlap = !(
          sectionRect.bottom < 0 ||
          sectionRect.top > window.innerHeight
        );
        
        // If there's both horizontal and vertical overlap, we have a collision
        if (horizontalOverlap && verticalOverlap) {
          hasCollision = true;
        }
      });
      
      setSideNavVisible(!hasCollision);
    };
    
    // Debounced check collision
    const debouncedCheckCollision = () => {
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
      
      collisionTimeoutRef.current = setTimeout(checkCollision, 100);
    };
    
    window.addEventListener('scroll', debouncedCheckCollision);
    window.addEventListener('resize', debouncedCheckCollision);
    
    // Initial check
    checkCollision();
    
    return () => {
      window.removeEventListener('scroll', debouncedCheckCollision);
      window.removeEventListener('resize', debouncedCheckCollision);
      
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
    };
  }, []);

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.values(sectionRefs);
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Set a minimum threshold of visibility for a section to be considered active
      // This helps with smaller sections
      const visibilityThreshold = 100; // px from top of viewport
      
      // Check each section
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // A section is active if its top is within the threshold from the viewport top
        // OR if we're within the section (between its top and bottom)
        if (
          (rect.top >= 0 && rect.top <= visibilityThreshold) ||
          (rect.top <= 0 && rect.bottom > visibilityThreshold)
        ) {
          setActiveSection(i);
          return;
        }
      }
      
      // If we get here, no section matches our primary criteria
      // Use a fallback approach based on which section occupies most of the viewport
      let maxVisibleSection = 0;
      let maxVisibleHeight = 0;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // Calculate how much of the section is visible in the viewport
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        
        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          maxVisibleSection = i;
        }
      }
      
      setActiveSection(maxVisibleSection);
    };
    
    // Throttle the scroll handler to improve performance and reduce sensitivity
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const throttledScrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 50); // 50ms delay
    };
    
    window.addEventListener('scroll', throttledScrollHandler);
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  const showcaseItems: CarouselItem[] = [
    {
      id: 1,
      title: translations.showcase.items.endings.title,
      description: translations.showcase.items.endings.description,
      image: "/images/projects/mystica/showCase1.png",
      alt: translations.showcase.items.endings.title
    },
    {
      id: 2,
      title: translations.showcase.items.dialogue.title,
      description: translations.showcase.items.dialogue.description,
      image: "/images/projects/mystica/showCase2.png",
      alt: translations.showcase.items.dialogue.title
    },
    {
      id: 3,
      title: translations.showcase.items.investigation.title,
      description: translations.showcase.items.investigation.description,
      image: "/images/projects/mystica/showCase3.png",
      alt: translations.showcase.items.investigation.title
    },
    {
      id: 4,
      title: translations.showcase.items.inventory.title,
      description: translations.showcase.items.inventory.description,
      image: "/images/projects/mystica/showCase4.png",
      alt: translations.showcase.items.inventory.title
    }
  ];

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      <div className="project-page mystica-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div style={languageSwitcherStyle}>
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1>
              <a 
                href="https://bellinimoon.itch.io/mystica-replica" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <DecryptedText 
                  text={translations.title}
                  animateOn="view"
                  sequential={true}
                  maxIterations={4}
                  speed={60}
                  className="decrypted-title"
                  parentClassName="title-container"
                  encryptedClassName="encrypted-title"
                />
              </a>
            </h1>
            <hr />
            <h2>
              <DecryptedText 
                text={translations.subtitle}
                animateOn="view"
                sequential={true}
                //useOriginalCharsOnly={true}
                maxIterations={1}
                speed={20}
                className="decrypted-description"
                parentClassName="description-container"
                encryptedClassName="encrypted-description"
              />
            </h2>
          </div>
        </header>

        <main className="project-content">
          <div className="container">
            {/* <SpotlightCard 
              className="project-section game-embed-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.play}>
                <h3>{translations.sections.play}</h3>
                <div className="game-embed-container">
                  <div className="itchio-embed-wrapper">
                    <iframe
                      src="https://bellinimoon.itch.io/mystica-replica/embed"
                      allowFullScreen
                      frameBorder="0"
                      title="Mystica Replica"
                      style={{
                        width: '100%',
                        height: '700px', // or use a CSS class if you'd prefer
                        maxWidth: '950px',
                        display: 'block',
                        margin: '0 auto',
                      }}
                    ></iframe>
                  </div>

                </div>
              </section>
            </SpotlightCard> */}

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.behind}>
                <h3>{translations.navItems[1]}</h3>
                <p>
                  {translations.sections.behindTheScene}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section showcase-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.showcase}>
                <h3>{translations.showcase.title}</h3>
                <div className="carousel-wrapper">
                  <Carousel 
                    items={showcaseItems}
                    baseWidth={800}
                    autoplay={true}
                    autoplayDelay={5000}
                    pauseOnHover={true}
                    loop={true}
                  />
                </div>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.gameplay}>
                <h3>{translations.navItems[3]}</h3>
                <p>
                  {translations.sections.gameplay}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.narrative}>
                <h3>{translations.navItems[4]}</h3>
                <p>
                  {translations.sections.narrative}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.challenges}>
                <h3>{translations.navItems[5]}</h3>
                <p>
                  {translations.sections.challenges}
                </p>
              </section>
            </SpotlightCard>
          </div>
        </main>

        <div 
          className="side-navigation" 
          ref={sideNavRef} 
          style={{ 
            opacity: sideNavVisible ? 1 : 0,
            visibility: sideNavVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
          }}
        >
          <AnimatedList
            items={translations.navItems}
            onItemSelect={(item, index) => {
              scrollToSection(item, index);
              setActiveSection(index);
            }}
            showGradients={false}
            enableArrowNavigation={true}
            className="mystica-nav"
            displayScrollbar={false}
            selectedIndex={activeSection}
          />
        </div>
      </div>
    </ClickSpark>
  );
};

export default MysticaReplica; 